'use client';

import clsx from 'clsx';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';

import { isNil } from '@/utils/isNil';

interface Props {
  children: ReactNode;
  actionComponent: ReactNode;
}

export const PlpFilterChipsWrapper: FC<Props> = ({ children, actionComponent }) => {
  const [isPinned, setIsPinned] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  // Manage switching the state from pinned to unpinned
  useEffect(() => {
    // Pinned intersection observer technique adapted from:
    // https://css-tricks.com/how-to-detect-when-a-sticky-element-gets-pinned/
    let observer: IntersectionObserver | null = null;
    if (ref.current && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        ([entry]) => {
          setIsPinned(entry.intersectionRatio < 1);
        },
        {
          root: null,
          threshold: [1],
        },
      );

      observer.observe(ref.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, [ref]);

  return (
    <div
      className={clsx(
        'pointer-events-none -mt-2 border-b border-b-bac-tie2 pb-4 md:sticky md:-top-px md:z-10 md:-mx-4 md:-mt-20 md:grid md:grid-cols-[1fr_162px] md:gap-4 md:px-4 md:pt-20',
        { 'border-b-bor-neu-mut bg-bac-tie2': isPinned },
      )}
      ref={ref}
    >
      <div className="pointer-events-auto min-w-0">{children}</div>
      {!isNil(actionComponent) && (
        <div className="pointer-events-auto hidden h-full justify-end md:flex">{actionComponent}</div>
      )}
    </div>
  );
};
